<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import DeleteButton from '@/components/DeleteButton.vue';
    import { useForm } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { toast } from 'vue-sonner';
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';

    const queryClient = useQueryClient();

    const emit = defineEmits(['deleted', 'error']);

    const { t } = useI18n();

    interface Props {
        route: string;
        queryKey: Array<string | number>;
        buttonText?: string;
        header?: string;
        description?: string;
        cancelButtonText?: string;
        continueButtonText?: string;
        successMessage?: string;
        errorMessage?: string;
        method?: 'delete' | 'post';
        requestData?: Record<string, any>;
    }

    const props = withDefaults(defineProps<Props>(), {
        header: 'forms.delete_confirmation.header',
        description: 'forms.delete_confirmation.description',
        cancelButtonText: 'forms.delete_confirmation.cancel',
        continueButtonText: 'forms.delete_confirmation.continue',
        successMessage: 'forms.delete_confirmation.success',
        errorMessage: 'forms.delete_confirmation.error',
        method: 'delete',
        requestData: () => ({}),
    });

    const form = useForm(props.requestData);

    const deleteAction = () => {
        form[props.method](props.route, {
            preserveScroll: true,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: props.queryKey });
                toast.success(t(props.successMessage));
                emit('deleted');
            },
            onError: (error) => {
                toast.error(error?.error || t(props.errorMessage));
                emit('error', error);
            },
        });
    };
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <DeleteButton :button-text="buttonText" />
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ $t(header) }}</AlertDialogTitle>
                <AlertDialogDescription>
                    <slot name="description">{{ $t(description) }}</slot>
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ $t(cancelButtonText) }}</AlertDialogCancel>
                <AlertDialogAction @click="deleteAction">
                    {{ $t(continueButtonText) }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
